import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, ExternalLink, ChevronLeft, ChevronRight, RotateCw, ZoomIn, ZoomOut, MousePointer, Copy, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useEnhancedTextSelection } from '@/hooks/useEnhancedTextSelection';
import { usePDFSearch } from '@/hooks/usePDFSearch';
import { usePDFPerformance } from '@/hooks/usePDFPerformance';
import PDFSearchPanel from '@/components/PDFSearchPanel';
import TextSelectionOverlay from '@/components/TextSelectionOverlay';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface SimplePDFViewerProps {
  pdfUrl?: string;
  fileName?: string;
}

const SimplePDFViewer: React.FC<SimplePDFViewerProps> = ({
  pdfUrl,
  fileName = "Document"
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState<number>(1.0);
  const [rotation, setRotation] = useState<number>(0);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const { toast } = useToast();
  
  const { 
    selectedText, 
    hasSelection, 
    selectionMetadata,
    showOverlay,
    copySelectedText, 
    extractSelectedText,
    selectAllText, 
    clearSelection 
  } = useEnhancedTextSelection({
    enableSmartHighlighting: true,
    enableAccessibility: true
  });

  const {
    searchTerm,
    setSearchTerm,
    searchResults,
    currentResult,
    isSearching,
    performSearch,
    nextResult,
    previousResult,
    clearSearch,
    hasResults
  } = usePDFSearch({
    numPages,
    onPageChange: setPageNumber
  });

  const {
    startPhase,
    endPhase,
    trackRenderStart,
    trackRenderEnd,
    optimizePDFUrl,
    getPerformanceReport
  } = usePDFPerformance();

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    endPhase('document-load');
    
    toast({
      title: "PDF Loaded",
      description: `Document loaded with ${numPages} page${numPages !== 1 ? 's' : ''}`,
      duration: 2000
    });
  }, [endPhase, toast]);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error);
    setError(error.message);
    setLoading(false);
    endPhase('document-load');
    
    toast({
      title: "PDF Load Error",
      description: "Failed to load PDF. Please try downloading or opening in a new tab.",
      variant: "destructive",
    });
  }, [endPhase, toast]);

  const handleDownload = useCallback(() => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.click();
    }
  }, [pdfUrl, fileName]);

  const handleOpenInNewTab = useCallback(() => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  }, [pdfUrl]);

  const handleSelectAll = useCallback(() => {
    selectAllText();
  }, [selectAllText]);

  const changePage = useCallback((offset: number) => {
    setPageNumber(prevPageNumber => Math.min(Math.max(prevPageNumber + offset, 1), numPages));
  }, [numPages]);

  const previousPage = useCallback(() => changePage(-1), [changePage]);
  const nextPage = useCallback(() => changePage(1), [changePage]);

  const zoomIn = useCallback(() => setScale(prev => Math.min(prev + 0.2, 3.0)), []);
  const zoomOut = useCallback(() => setScale(prev => Math.max(prev - 0.2, 0.5)), []);
  const rotate = useCallback(() => setRotation(prev => (prev + 90) % 360), []);

  const handleExtractText = useCallback(() => {
    const extracted = extractSelectedText();
    if (extracted) {
      toast({
        title: "Text Extracted",
        description: `Extracted ${extracted.metadata.wordCount} words from page ${extracted.metadata.pageNumber || 'unknown'}`,
        duration: 3000
      });
      // Here you could save to a notes system, export, etc.
      return extracted;
    }
  }, [extractSelectedText, toast]);

  const handleToggleSearch = useCallback(() => {
    setShowSearch(prev => !prev);
    if (showSearch) {
      clearSearch();
    }
  }, [showSearch, clearSearch]);

  // Performance tracking
  useEffect(() => {
    if (pdfUrl) {
      startPhase('document-load');
    }
  }, [pdfUrl, startPhase]);

  // Responsive scaling
  useEffect(() => {
    const handleResize = () => {
      const container = document.querySelector('.pdf-container');
      if (container) {
        const containerWidth = container.clientWidth;
        const optimalScale = Math.min(1.0, containerWidth / 800); // Adjust based on your needs
        setScale(optimalScale);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial call

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'f':
            event.preventDefault();
            handleToggleSearch();
            break;
          case '=':
          case '+':
            event.preventDefault();
            zoomIn();
            break;
          case '-':
            event.preventDefault();
            zoomOut();
            break;
        }
      } else {
        switch (event.key) {
          case 'ArrowLeft':
            if (!showSearch) {
              event.preventDefault();
              previousPage();
            }
            break;
          case 'ArrowRight':
            if (!showSearch) {
              event.preventDefault();
              nextPage();
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleToggleSearch, zoomIn, zoomOut, previousPage, nextPage, showSearch]);

  if (!pdfUrl) {
    return (
      <Card className="flex-1 flex items-center justify-center">
        <CardContent className="text-center text-muted-foreground">
          No PDF available
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="flex-1 flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="text-sm font-medium text-destructive">Failed to load PDF</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
            <Button size="sm" variant="outline" onClick={handleOpenInNewTab}>
              <ExternalLink className="h-4 w-4 mr-1" />
              Open
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p className="mb-2">PDF could not be displayed</p>
            <p className="text-sm">Error: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex-1 flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium">{fileName}</div>
          {hasSelection && (
            <Badge variant="secondary" className="text-xs">
              {selectedText.length} chars selected
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {hasSelection && (
            <Button size="sm" variant="default" onClick={() => copySelectedText('plain')}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleSelectAll}>
            <MousePointer className="h-4 w-4 mr-1" />
            Select All
          </Button>
          <Button 
            size="sm" 
            variant={showSearch ? "default" : "outline"} 
            onClick={handleToggleSearch}
          >
            <Search className="h-4 w-4 mr-1" />
            Search
          </Button>
          <Button size="sm" variant="outline" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
          <Button size="sm" variant="outline" onClick={handleOpenInNewTab}>
            <ExternalLink className="h-4 w-4 mr-1" />
            Open
          </Button>
        </div>
      </CardHeader>

      {/* Search Panel */}
      {showSearch && (
        <PDFSearchPanel
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onSearch={performSearch}
          searchResults={searchResults}
          currentResultIndex={searchResults.findIndex(r => r === currentResult)}
          onNextResult={nextResult}
          onPreviousResult={previousResult}
          onClearSearch={clearSearch}
          isSearching={isSearching}
          className="mx-4"
        />
      )}
      
      {/* Controls */}
      {!loading && numPages > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={previousPage} disabled={pageNumber <= 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {pageNumber} of {numPages}
            </span>
            <Button size="sm" variant="outline" onClick={nextPage} disabled={pageNumber >= numPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={zoomOut} disabled={scale <= 0.5}>
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm">{Math.round(scale * 100)}%</span>
            <Button size="sm" variant="outline" onClick={zoomIn} disabled={scale >= 3.0}>
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={rotate}>
              <RotateCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <CardContent className="flex-1 overflow-auto flex justify-center items-start p-4 pdf-container relative">
        {loading && (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <div className="text-muted-foreground">Loading PDF...</div>
            <div className="text-xs text-muted-foreground">Please wait while we prepare your document</div>
          </div>
        )}
        
        <Document
          file={optimizePDFUrl(pdfUrl || '')}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
          error=""
          noData=""
          className="max-w-full"
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            rotate={rotation}
            renderTextLayer={true}
            renderAnnotationLayer={false}
            className="shadow-lg pdf-page-container"
            data-page-number={pageNumber}
            onRenderSuccess={trackRenderEnd}
            onLoadSuccess={trackRenderStart}
          />
        </Document>

        {/* Text Selection Overlay */}
        <TextSelectionOverlay
          selectedText={selectedText}
          isVisible={showOverlay && hasSelection}
          onCopy={() => copySelectedText('rich')}
          onExtract={handleExtractText}
          onClear={clearSelection}
          position={selectionMetadata.selectionRect ? {
            x: selectionMetadata.selectionRect.left,
            y: selectionMetadata.selectionRect.top
          } : undefined}
        />
      </CardContent>
    </Card>
  );
};

export default SimplePDFViewer;